import React, { useContext, useEffect, useState } from 'react';
import { Box, Stack, Typography, Button, Input, Card, Chip, Divider, Alert, IconButton, Badge, Tooltip, Tab, TabList, TabPanel, Tabs, FormControl, FormLabel, Sheet, CircularProgress } from '@mui/joy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PublicIcon from '@mui/icons-material/Public';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LinkIcon from '@mui/icons-material/Link';
import ClearIcon from '@mui/icons-material/Clear';
import { LayoutContext } from '../../context';




const RegionManagerModal = ({ regions, selectedRegion, onSelectRegion, onSaveRegion, citiesWithoutRegion, onAssignCity, onUnassignCity, editingRegion, onEditRegion, onCancelEdit, loading, onClose, onDeleteRegion, loadingMore, observerTarget, pagination, onLoadMore }) => {
  const { citys, setCitys } = useContext(LayoutContext);
  const [newRegionName, setNewRegionName] = useState('');
  const [editingName, setEditingName] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [searchCity, setSearchCity] = useState('');
  const [filteredCities, setFilteredCities] = useState(citiesWithoutRegion || []);


  // Эффект для фильтрации городов при поиске
  useEffect(() => {
    if (searchCity.trim() === '') {
      setFilteredCities(citiesWithoutRegion || []);
    } else {
      const filtered = (citiesWithoutRegion || []).filter(city =>
        city.name.toLowerCase().includes(searchCity.toLowerCase())
      );
      setFilteredCities(filtered);
    }
  }, [searchCity, citiesWithoutRegion]);

  // Обработчик привязки города с обновлением UI
  const handleAssignCity = async (cityId) => {
    // Находим город в списке
    const cityToAssign = citiesWithoutRegion.find(city => city.id === cityId);
    
    // Сразу обновляем UI (оптимистичное обновление)
    if (cityToAssign && editingRegion) {
      // Добавляем город к региону в UI
      const updatedRegion = {
        ...editingRegion,
        cities: [...(editingRegion.cities || []), cityToAssign]
      };
      
      // Убираем город из списка без региона
      const updatedCitiesWithoutRegion = citiesWithoutRegion.filter(city => city.id !== cityId);
      
      // Обновляем состояния
      onEditRegion(updatedRegion);
      // Можно передать callback для обновления citiesWithoutRegion в родителе
    }
    
    // Вызываем API функцию
    await onAssignCity(cityId);
  };

  // Обработчик отвязки города с обновлением UI
  const handleUnassignCity = async (cityId) => {
    // Находим город в списке региона
    const cityToUnassign = editingRegion?.cities?.find(city => city.id === cityId);
    
    // Сразу обновляем UI (оптимистичное обновление)
    if (cityToUnassign && editingRegion) {
      // Убираем город из региона в UI
      const updatedRegion = {
        ...editingRegion,
        cities: editingRegion.cities.filter(city => city.id !== cityId)
      };
      
      // Добавляем город в список без региона
      // Обновляем состояния
      onEditRegion(updatedRegion);
    }
    
    // Вызываем API функцию
    await onUnassignCity(cityId);
  };

  // Обработчик создания нового региона
  const handleCreateRegion = () => {
    if (!newRegionName.trim()) return;
    
    onSaveRegion({
      name: newRegionName.trim()
    });
    setNewRegionName('');
  };

  // Добавляем обработчик удаления региона
  const handleDeleteRegion = (region) => {
    if (window.confirm(`Вы уверены, что хотите удалить регион "${region.name}"?\n\nВнимание: Регион будет удален, но все привязанные к нему города останутся в системе и будут отвязаны от региона.`)) {
      onDeleteRegion(region.id);
    }
  };

  // Обработчик обновления региона
  const handleUpdateRegion = () => {
    if (!editingRegion || !editingName.trim()) return;
    
    onSaveRegion({
      id: editingRegion.id,
      name: editingName.trim()
    });
  };

  // Если редактируем регион, показываем форму редактирования
  if (editingRegion) {
    return (
      <Stack spacing={2}>
        <Sheet variant='soft' color="neutral" sx={{ p: 2, borderRadius: 'lg' }}>
          <FormControl sx={{ flex: 1 }} required>
            <FormLabel>Название региона</FormLabel>
            <Input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              placeholder="Название региона"
              defaultValue={editingRegion.name}
            />
          </FormControl>
        </Sheet>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="outlined" onClick={onCancelEdit}>
            Отмена
          </Button>
          <Button 
            variant="soft" 
            color="primary"
            onClick={handleUpdateRegion}
            disabled={!editingName.trim()}
          >
            Сохранить изменения
          </Button>
        </Stack>
        
        {/* Города в регионе */}
        {editingRegion.cities && editingRegion.cities.length > 0 && (
          <>
            <Typography level="title-md" sx={{ mt: 2 }}>
              Города в регионе ({editingRegion.cities.length})
            </Typography>
            <Stack spacing={1}>
              {editingRegion.cities.map(city => (
                <Card key={city.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationCityIcon fontSize="small" />
                      <Typography level="body-sm">{city.name}</Typography>
                    </Stack>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="danger"
                      startDecorator={<LinkOffIcon />}
                      onClick={() => handleUnassignCity(city.id)}
                    >
                      Отвязать
                    </Button>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </>
        )}

        {/* Города без региона */}
        {citiesWithoutRegion.length > 0 && (
          <>
            <Typography level="title-md" sx={{ mt: 2 }}>
              Города без региона ({citiesWithoutRegion.length})
            </Typography>

            {/* Поле поиска */}
            <Input
              value={searchCity}
              onChange={(e) => setSearchCity(e.target.value)}
              placeholder="Поиск городов..."
              sx={{ mb: 2 }}
              endDecorator={
                searchCity && (
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={() => setSearchCity('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }
            />
            
            <Stack spacing={1}>
              {filteredCities.length === 0 ? (
                <Alert color="warning">
                  {searchCity ? 'Города не найдены' : 'Нет городов без региона'}
                </Alert>
                ) : (filteredCities.map(city => (
                  <Card key={city.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationCityIcon fontSize="small" />
                        <Typography level="body-sm">{city.name}</Typography>
                      </Stack>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="success"
                        startDecorator={<LinkIcon />}
                        onClick={() => handleAssignCity(city.id)}
                      >
                        Привязать
                      </Button>
                    </Stack>
                  </Card>
                ))
              )}
            </Stack>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Создание нового региона */}
      <Card variant="soft" color="neutral" sx={{ p: 2, borderRadius: 'lg' }}>
        <Typography level="title-sm">
          Создать новый регион
        </Typography>
        <Stack direction="row" spacing={1}>
          <Input
            value={newRegionName}
            onChange={(e) => setNewRegionName(e.target.value)}
            placeholder="Введите название региона"
            sx={{ flex: 1 }}
          />
          <Button
            variant="solid"
            color="primary"
            onClick={handleCreateRegion}
            disabled={!newRegionName.trim()}
            startDecorator={<AddIcon />}
          >
            Создать
          </Button>
        </Stack>
      </Card>

      <Tabs value={activeTab} onChange={(event, value) => setActiveTab(value)}>
        <TabList>
          <Tab sx={{ lineHeight: 1 }}>Все регионы ({regions.length})</Tab>
          {citiesWithoutRegion.length > 0 && (
            <Tab sx={{ lineHeight: 1 }}>
              Не привязанные города ({citiesWithoutRegion.length})
            </Tab>
          )}
          <Tab sx={{ lineHeight: 1 }}>Все города ({citys?.length})</Tab>
        </TabList>

        {/* Вкладка со всеми регионами */}
        <TabPanel value={0}>
          {loading ? (
            <Alert color="neutral">Загрузка регионов...</Alert>
          ) : regions.length === 0 ? (
            <Alert color="warning">Нет созданных регионов</Alert>
          ) : (
            <Stack spacing={1}>
              {regions.map(region => (
                <Card
                  key={region.id}
                  variant={selectedRegion?.id === region.id ? "soft" : "outlined"}
                  sx={{
                    borderColor: selectedRegion?.id === region.id ? 'primary.500' : undefined
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={2} alignItems="center">
                      {selectedRegion?.id === region.id ? (
                        <CheckCircleIcon color="primary" />
                      ) : (
                        <PublicIcon />
                      )}
                      <Stack sx={{ flex: 1 }}>
                        <Typography level="title-sm">
                          {region.name}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {region.cities_count || 0} городов
                        </Typography>
                        
                        {/* Список городов в регионе */}
                        {region.cities && region.cities.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {region.cities.map(city => (
                              <Chip
                                key={city.id}
                                size="sm"
                                variant="outlined"
                                color="neutral"
                                startDecorator={<LocationCityIcon fontSize="small" />}
                              >
                                {city.name}
                              </Chip>
                            ))}
                          </Box>
                        )}
                      </Stack>
                    </Stack>
                    
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="sm"
                        variant={selectedRegion?.id === region.id ? "solid" : "outlined"}
                        color="primary"
                        onClick={() => {
                          onSelectRegion(region);
                          onClose();
                        }}
                      >
                        {selectedRegion?.id === region.id ? 'Выбран' : 'Выбрать'}
                      </Button>
                      <Tooltip title="Редактировать">
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditRegion(region);
                            setEditingName(region.name);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить регион">
                        <IconButton
                          size="sm"
                          variant="outlined"
                          color="danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRegion(region);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Card>
              ))}
            </Stack>
          )}
          {!editingRegion && regions.length > 0 && (
            <>
                {/* Индикатор загрузки */}
                {loadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size="sm" />
                        <Typography level="body-sm" sx={{ ml: 2 }}>
                            Загрузка...
                        </Typography>
                    </Box>
                )}
                
                {/* Триггер для подгрузки */}
                {pagination?.has_next && !loadingMore && (
                    <div ref={observerTarget} style={{ height: '1px' }} />
                )}
                
                {/* Индикатор конца списка */}
                {!pagination?.has_next && regions.length > 0 && (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography level="body-sm" color="neutral">
                            Все регионы загружены ({pagination?.total_count || regions.length})
                        </Typography>
                    </Box>
                )}
            </>
        )}
        </TabPanel>

        {/* Вкладка с городами без региона */}
        <TabPanel value={1}>
          {citiesWithoutRegion.length === 0 ? (
            <Alert color="success">Все города привязаны к регионам</Alert>
          ) : (
            <>
              <Stack spacing={1}>
                {citiesWithoutRegion.map(city => (
                  <Card key={city.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationCityIcon fontSize="small" />
                        <Typography level="body-sm">{city.name}</Typography>
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </TabPanel>

        {/* Вкладка с городами всеми */}
        <TabPanel value={2}>
          {citys?.length === 0 ? (
            <Alert color="success">Города не загружены</Alert>
          ) : (
            <>
              <Stack spacing={1}>
                {citys?.sort((a, b) => {if (a.name < b.name) return -1; if (a.name > b.name) return 1; return 0;})?.map(city => (
                  <Card key={city.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationCityIcon fontSize="small" />
                        <Typography level="body-sm">{city.name}</Typography>
                      </Stack>
                    </Stack>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </TabPanel>
      </Tabs>
    </Stack>
  );
};

export default RegionManagerModal;